import { FC, ChangeEvent, useEffect, useRef, useState } from 'react';
import { useModal } from '@blueprint/ui';
import { ButtonWithIcon } from '@zoopla/blueprint-button-with-icon';
import { CloseSmall } from '@blueprint/icons';
import { useUserSetOptimizelyOverrides } from '../hooks/useUserSetOptimizelyOverrides';
import {
    useLazyOptimizelyExperiment,
    useLazyOptimizelyFeatureFlag
} from '../hooks/useDecide';

import { OptimizelyLogo } from './OptimizelyLogo';
import {
    StyledTable,
    Container,
    FixedButtonWithIcon,
    Overlay
} from './DevToggle.styles';

type Entry = Record<string, unknown> & {
    key: string;
    variations?: Record<string, unknown>[];
};
type onForceHandler = (urlParam: [string, string]) => void;

const getHeadings = (arrayOfObjects: Record<string, unknown>[]) => {
    return arrayOfObjects.reduce(
        (acc, obj) => {
            const k = Object.keys(obj);
            k.forEach((v) => {
                if (acc.indexOf(v) === -1) acc.push(v);
            });
            return acc;
        },
        ['key', 'session_state']
    );
};

const ForceDropdown: FC<
    React.PropsWithChildren<{
        entry: Entry;
        entryState: unknown;
        onForce: onForceHandler;
    }>
> = ({ entry, entryState, onForce }) => {
    const isFlag = typeof entryState === 'boolean';
    const printState = JSON.stringify(entryState);
    const variants = isFlag
        ? ['true', 'false', 'cancel']
        : [
              ...(entry?.variations?.map(({ key }) => key as string) ?? []),
              'cancel'
          ];
    const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        const isCancel = val === 'cancel';
        const newVal = isCancel ? (isFlag ? 'true' : entryState) : val;
        const urlParam = `${isCancel ? 'cancel_' : ''}${
            isFlag ? 'force_feature_flag_' : 'force_test_variation_'
        }${entry.key}`;
        onForce([urlParam, newVal as string]);
    };
    return (
        <select
            name={`override_${entry.key}`}
            onChange={onChange}
            defaultValue={entryState as string}
        >
            <option value={''} disabled>
                current state: {printState}
            </option>
            {variants.map((v) => (
                <option key={v} value={v}>
                    {v}
                </option>
            ))}
        </select>
    );
};

const ForceExperimentDropdown: FC<
    React.PropsWithChildren<{
        entry: Entry;
        onForce: onForceHandler;
    }>
> = ({ entry, onForce }) => {
    const [isLoading, entryState] = useLazyOptimizelyExperiment(entry.key, {
        disableTracking: true
    });
    if (isLoading) return null;
    return (
        <ForceDropdown
            entry={entry}
            entryState={entryState}
            onForce={onForce}
        />
    );
};

const ForceFlagDropdown: FC<
    React.PropsWithChildren<{
        entry: Entry;
        onForce: onForceHandler;
    }>
> = ({ entry, onForce }) => {
    const [isLoading, isFlagEnabled] = useLazyOptimizelyFeatureFlag(entry.key, {
        disableTracking: true
    });
    if (isLoading) return null;
    return (
        <ForceDropdown
            entry={entry}
            entryState={isFlagEnabled}
            onForce={onForce}
        />
    );
};

type OptimizelyTestType = 'experiments' | 'flags';

const TableRow: FC<
    React.PropsWithChildren<{
        entry: Entry;
        forcedKeys?: string[];
        headings: string[];
        onForce: onForceHandler;
        type: OptimizelyTestType;
    }>
> = ({ entry, forcedKeys, headings, onForce, type }) => {
    const isForced = forcedKeys?.includes(entry.key);
    return (
        <tr className={isForced ? 'forced' : ''}>
            {headings.map((k: string) => (
                <td key={entry['id'] + k}>
                    {k === 'session_state' ? (
                        type === 'experiments' ? (
                            <ForceExperimentDropdown
                                entry={entry}
                                onForce={onForce}
                            />
                        ) : (
                            <ForceFlagDropdown
                                entry={entry}
                                onForce={onForce}
                            />
                        )
                    ) : (
                        JSON.stringify(entry[k])
                    )}
                </td>
            ))}
        </tr>
    );
};

const TableView: FC<
    React.PropsWithChildren<{
        data: Entry[];
        forcedKeys?: string[];
        title: OptimizelyTestType;
        onForce: onForceHandler;
    }>
> = ({ data, forcedKeys, title, onForce }) => {
    const headings = getHeadings(data);
    return (
        <StyledTable>
            <thead>
                <tr>
                    <td colSpan={headings.length}>{title}</td>
                </tr>
                <tr>
                    {headings.map((key) => (
                        <th key={key}>{key}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((entry) => (
                    <TableRow
                        key={entry['id'] as string}
                        type={title}
                        entry={entry}
                        onForce={onForce}
                        headings={headings}
                        forcedKeys={forcedKeys}
                    />
                ))}
            </tbody>
        </StyledTable>
    );
};

const OptimizelyDevToggles = () => {
    const [dataFile, setDataFile] =
        useState<{ experiments: Entry[]; featureFlags: Entry[] }>();
    const triggerOrigin = useRef(null);
    const [isOverlayVisible, openOverlay, closeOverlay] = useModal();
    const userSetOverrides = useUserSetOptimizelyOverrides();

    useEffect(() => {
        async function fetchDataFile() {
            try {
                const res = await fetch(
                    `${process.env['NEXT_PUBLIC_OPTIMIZELY_URL']}/datafiles/${process.env['NEXT_PUBLIC_OPTIMIZELY_KEY']}.json`
                );
                if (res.ok) {
                    const dataFile = await res.json();
                    setDataFile(dataFile);
                }
            } catch (e) {
                // do nothing
            }
        }
        fetchDataFile();
    }, []);

    if (!dataFile) return null;

    const onForce: onForceHandler = ([param, val]) => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set(param, val);
        window.location.search = urlParams.toString();
    };

    const forcedFlags = Object.keys(userSetOverrides?.features || {});
    const forcedExperiments = Object.keys(userSetOverrides?.experiments || {});
    const forcedKeys = [...forcedFlags, ...forcedExperiments];

    return (
        <aside aria-label="optimizely developer tools">
            <FixedButtonWithIcon
                iconOnly
                aria-label="optimizely"
                variant="secondary"
                onClick={() => openOverlay(triggerOrigin)}
                ref={triggerOrigin}
            >
                <OptimizelyLogo />
            </FixedButtonWithIcon>
            <Overlay isShowing={isOverlayVisible}>
                <Container>
                    <ButtonWithIcon
                        aria-label="close"
                        variant="tertiary"
                        iconOnly
                        onClick={closeOverlay}
                        style={{ float: 'right' }}
                    >
                        <CloseSmall />
                    </ButtonWithIcon>
                    <TableView
                        title="experiments"
                        data={dataFile.experiments}
                        forcedKeys={forcedKeys}
                        onForce={onForce}
                    />
                    <TableView
                        title="flags"
                        data={dataFile.featureFlags}
                        forcedKeys={forcedKeys}
                        onForce={onForce}
                    />
                </Container>
            </Overlay>
        </aside>
    );
};

export default OptimizelyDevToggles;
